
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { waitlistFormSchema, WaitlistFormValues } from "@/components/waitlist/schema";
import { useAuth } from "@/components/auth/AuthProvider";
import { useWaitlistPreferences } from "@/hooks/use-waitlist-preferences";
import { supabase } from "@/integrations/supabase/client";

interface UseWaitlistFormProps {
  countyName?: string;
  nursingHomeName?: string;
}

export function useWaitlistForm({ countyName, nursingHomeName }: UseWaitlistFormProps) {
  const { user } = useAuth();
  const { savedPreferences } = useWaitlistPreferences();
  const [formInitialized, setFormInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      preferredLocation: countyName ? [countyName.toLowerCase().replace(/\s+/g, '-')] : [],
      specificNursingHome: nursingHomeName || "",
      careNeeds: [],
      careNeedsDetails: "",
      mobilityRequirements: "",
      residentRelation: "parent",
      residentSalutation: undefined,
      residentLastName: "",
      residentAge: "",
      timeframe: "researching",
      fairDealScheme: "need-info",
      contactPreference: "email",
      additionalInformation: "",
      consentToPrivacyPolicy: false,
    },
  });

  // Initialize form with saved preferences or user data
  useEffect(() => {
    if (formInitialized || !user) return;

    const initForm = async () => {
      // Load saved preferences if available
      if (savedPreferences) {
        Object.entries(savedPreferences).forEach(([key, value]) => {
          if (key in form.getValues()) {
            const formKey = key as keyof WaitlistFormValues;
            form.setValue(formKey, value);
          }
        });
        
        toast.info("Your previous preferences have been restored");
        setFormInitialized(true);
        return;
      }

      // Try to auto-fill from onboarding preferences
      const { data: profile } = await supabase
        .from("profiles")
        .select("saved_preferences, first_name, last_name")
        .eq("id", user.id)
        .maybeSingle();

      const userMeta = user.user_metadata;
      form.setValue("firstName", userMeta?.first_name || profile?.first_name || "");
      form.setValue("lastName", userMeta?.last_name || profile?.last_name || "");
      form.setValue("email", user.email || "");
      form.setValue("phone", userMeta?.phone || "");

      // Auto-fill from onboarding wizard data
      if (profile?.saved_preferences && typeof profile.saved_preferences === "object") {
        const prefs = profile.saved_preferences as Record<string, unknown>;
        
        if (Array.isArray(prefs.careNeeds) && prefs.careNeeds.length > 0) {
          form.setValue("careNeeds", prefs.careNeeds as string[]);
        }
        if (typeof prefs.timeframe === "string" && prefs.timeframe) {
          form.setValue("timeframe", prefs.timeframe as WaitlistFormValues["timeframe"]);
        }
        if (Array.isArray(prefs.counties) && prefs.counties.length > 0 && !countyName) {
          form.setValue("preferredLocation", prefs.counties as string[]);
        }
        if (Array.isArray(prefs.priorities) && prefs.priorities.includes("fair_deal")) {
          form.setValue("fairDealScheme", "applied");
        }

        toast.info("We've pre-filled some fields from your preferences", {
          description: "Review and update as needed."
        });
      }

      setFormInitialized(true);
    };

    initForm();
  }, [user, form, formInitialized, savedPreferences, countyName]);

  return {
    form,
    isSubmitting,
    setIsSubmitting,
    user
  };
}
